import moment from 'moment'
import { useState } from 'react'
import { Card, OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap'

import useCallApi from '../../../hooks/useCallApi'
import Loading from '../../../templates/UI/Loading/Loading'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Affiliate from '../../../templates/Affiliate/Affiliate'
import callAPI from '../../../helpers/callAPI'
import CompliancePaymentInfoCheck from '../../Dashboard/components/CompliancePaymentInfoCheck/CompliancePaymentInfoCheck'
import getDecimalNumber from '../../../helpers/getDecimalNumber'
import styles from '../../../css/backgroundClass.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useDateRangeFilter from '../../../hooks/useDateRangeFilter'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import DateRangeFilter from '../../Reports/Filters/DateRangeFilter/DateRangeFilter'
import { useQuery } from '../../../hooks/useQuery'

const AffiliateDetailMatchingInformation = ({ queryKey, queryValue, queryAffiliateId, showModal, handleCloseModal, isReviewed, onReviewChange }) => {
    document.title = 'Affiliate Matching Information'
    const key = queryKey
    const value = queryValue
    const affiliate_id = queryAffiliateId

    const { data, dataLoaded, refetch } = useCallApi(`compliance/affiliateMatchingInformation?affiliate_id=${affiliate_id}&matched_key=${encodeURIComponent(key)}&matched_value=${encodeURIComponent(value)}`, 'GET', {}, [key, value, affiliate_id])

    const whitelistValue = async (matchedAffiliateIds) => {
        try {
            const response = await callAPI(
                'compliance/affiliateMatchingInformation/whitelist',
                'POST',
                {
                    matched_key: key,
                    matched_value: value
                }
            )

            if (response.success) {
                refetch()
                onReviewChange(true)
            }            

            return response
        } catch (error) {
            console.error('Error marking affiliates as not matching:', error)
            throw error
        }
    }

    const transformData = (rawData) => {
        // Calculate global counts for each key-value combination
        const globalCounts = {}

        rawData.forEach(row => {
            if (row.matched_key && row.matched_value) {
                const countKey = `${row.matched_key}:${row.matched_value}`
                globalCounts[countKey] = (globalCounts[countKey] || 0) + 1
            }
        })

        // Create main affiliate row (first row)
        const mainAffiliateRow = rawData.length > 0 ? {
            ...rawData[0],
            matched_affiliate_id: null, // No matched ID for main affiliate
        } : null

        // Create rows for matched affiliates
        const matchedRows = rawData.map(row => ({
            ...row,
            affiliate_name: row.matched_name,
            affiliate_id: row.matched_affiliate_id,
            status: row.matched_status,
            aff_priority: row.matched_aff_priority,
            registration_date: row.matched_registration_date,
        }))

        // Combine: main affiliate first, then matched affiliates
        const orderedData = mainAffiliateRow ? [mainAffiliateRow, ...matchedRows] : matchedRows

        return orderedData.map(row => {
            const rowWithCounts = {
                ...row,
                valueCounts: {}
            }

            if (row.matched_key && row.matched_value) {
                const countKey = `${row.matched_key}:${row.matched_value.decrypted}`
                rowWithCounts.valueCounts[row.matched_key] = globalCounts[countKey]
            }

            return rowWithCounts
        })
    }

    const getUniqueKeys = (rawData) => {
        const keys = new Set()
        rawData.forEach(row => {
            if (row.matched_key) {
                keys.add(row.matched_key)
            }
        })
        return Array.from(keys)
    }

    const getReportOutput = () => {
        const transformedData = transformData(data.matchingInformation)
        const uniqueKeys = getUniqueKeys(data.matchingInformation)

        let columns = [
            {
                Header: 'Affiliate ID',
                accessor: 'affiliate_id',
            },
            {
                Header: 'Affiliate',
                accessor: 'affiliate_name',
                Cell: (tableData) => {
                    return <Affiliate aid={tableData.row.original.affiliate_id} name={tableData.row.original.affiliate_name} status={tableData.row.original.status} priority={tableData.row.original.aff_priority} />
                },
            },
            {
                Header: 'Registration Date',
                accessor: 'registration_date',
                Cell: (tableData) => {
                    return <>{moment.utc(tableData.row.original.registration_date).local().format('YYYY-MM-DD HH:mm:ss')}</>
                },
            },
            {
                Header: 'Matched Key',
                accessor: 'matched_key',
                Cell: (tableData) => {
                    return <>{tableData.row.original.matched_key || '-'}</>
                },
            },
            {
                Header: 'Matched Value',
                accessor: 'matched_value.decrypted',
            },
        ]

        return (
            <ReportTable
                data={transformedData}
                columns={columns}
                disableFilters
            />
        )
    }

    return (
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
            <Modal.Header closeButton>
                <div className='mb-h1-dark d-flex justify-content-between align-items-center w-100'>
                    <div>
                        {(dataLoaded && data.success) ? (
                            <>
                                <Affiliate aid={data.matchingInformation[0].affiliate_id} name={data.matchingInformation[0].affiliate_name} />
                                <span className="font-weight-normal">Matching Information where:</span> {key} = {data.matchingInformation[0].matched_value.decrypted}
                            </>
                        ) : (
                            'Affiliate Matching Details'
                        )}
                    </div>
                    <div>
                        <Button variant='success' className='ml-3' size='sm' onClick={whitelistValue}> Whitelist All Matches</Button>
                        <Button 
                            variant={isReviewed ? 'success' : 'warning'} 
                            className='ml-2' 
                            size='sm' 
                            onClick={() => onReviewChange(!isReviewed)}
                        >
                            {isReviewed ? 'Reviewed ✓' : 'Mark as Reviewed'}
                        </Button>
                    </div>
               </div>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {dataLoaded ? (
                    data.success ? (
                        getReportOutput()
                    ) : (
                        <div className="alert alert-danger" role="alert">
                            {data.message}
                        </div>
                    )
                ) : (
                    <Loading />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AffiliateDetailMatchingInformation
